/* eslint-disable complexity */
import { ChevronSmallDown, ChevronSmallUp, type SvgIconT } from '@elliemae/ds-icons';
import React, { useCallback, useContext, useState } from 'react';
import { ControlledDateTimePickerDatatestid } from '../../../constants/index.js';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import { convertToPositiveNumberIfPossible } from '../../../utils/numberHelpers.js';
import { StyledTimeBtn, StyledWheelChangeTimeBtn, StyledWheelList, StyledWheelListItem } from './Styleds.js';
import { TimeWheelContext } from './TimeWheelContext.js';

export const MinutesList = (): JSX.Element => {
  const {
    currHourNum,
    currMinuteNum,
    currMeridiem,
    visibleMinutes,
    handlePrevMinute,
    handleNextMinute,
    handleTimeWheelBtnChangeMinutes,
    handleCurrMinutesOnKeyDown,
  } = useContext(TimeWheelContext);
  const {
    prevMinutesBtnRef,
    currMinutesBtnRef,
    nextMinutesBtnRef,
    getIsDisabledTime,
    minutes,
    currFocusDescriber,
    latestInteractionRegion,
    trackFocusTimewheelCurrMinute,
    getProps,
    props: { readOnly, applyAriaDisabled },
  } = useContext(ControlledDateTimePickerContext);

  const handleCurrMeridiemRef = useCallback(
    (ButtonDomNode: HTMLDivElement) => {
      setTimeout(() => {
        currMinutesBtnRef.current = ButtonDomNode;
        if (latestInteractionRegion === 'timewheel' && currFocusDescriber === 'timewheel-curr-minute')
          ButtonDomNode?.focus?.();
      });
    },
    [currFocusDescriber, latestInteractionRegion, currMinutesBtnRef],
  );

  const [isFocusedWheel, setIsFocusedWheel] = useState(false);

  const handleWheelFocus = useCallback(() => {
    setIsFocusedWheel(true);
    trackFocusTimewheelCurrMinute();
  }, [setIsFocusedWheel, trackFocusTimewheelCurrMinute]);

  const handleWheelBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsFocusedWheel(false);
    },
    [setIsFocusedWheel],
  );

  const hasDisabledStyles = readOnly || applyAriaDisabled;

  const arrowColor = !hasDisabledStyles ? (['brand-primary', '700'] as SvgIconT.ColorType) : undefined;
  return (
    <StyledWheelList onFocus={handleWheelFocus} onBlur={handleWheelBlur}>
      <StyledWheelListItem>
        <StyledWheelChangeTimeBtn
          aria-label="subtract one minute"
          buttonType="raw"
          size="m"
          onClick={handlePrevMinute}
          innerRef={prevMinutesBtnRef}
          tabIndex={-1}
          data-testid={ControlledDateTimePickerDatatestid.TIMEWHEEL.PREV_MINUTE}
          applyAriaDisabled={applyAriaDisabled}
          readOnly={readOnly}
          type="button"
          getOwnerProps={getProps}
        >
          <ChevronSmallUp color={arrowColor} />
        </StyledWheelChangeTimeBtn>
      </StyledWheelListItem>
      {visibleMinutes.map((minutesString) => {
        const key = `timewheel-minutes-${minutesString || ''}`;
        if (!minutesString) return <StyledWheelListItem key={key} />;
        const currMinutesTimeString = `${currHourNum}:${minutesString} ${currMeridiem}`;
        const isEmptyCurrValueCurrentListItem =
          !minutes && convertToPositiveNumberIfPossible(minutesString) === currMinuteNum;
        const isTheItemVisuallyInTheCenter = isEmptyCurrValueCurrentListItem || minutes === minutesString;
        const isNotAValidSelection = getIsDisabledTime(currMinutesTimeString);

        const tabIndex = isTheItemVisuallyInTheCenter ? 0 : -1;
        const isSelected = minutes.length === 2 && convertToPositiveNumberIfPossible(minutesString) === currMinuteNum;
        const btnProps = {
          role: 'spinbutton',
          ...(isSelected && {
            'aria-valuenow': convertToPositiveNumberIfPossible(minutesString),
            'aria-valuetext': `${minutesString} minutes`,
          }),
          'aria-label': `${minutesString} minutes`,
          'aria-valuemin': 0,
          'aria-valuemax': 59,
          'aria-disabled': readOnly || applyAriaDisabled,
          'aria-invalid': isNotAValidSelection,
          'aria-readonly': readOnly || applyAriaDisabled,
          onKeyDown: isTheItemVisuallyInTheCenter ? handleCurrMinutesOnKeyDown : undefined,
          innerRef: isTheItemVisuallyInTheCenter ? handleCurrMeridiemRef : undefined,
        };
        return (
          <StyledWheelListItem key={key}>
            <StyledTimeBtn
              $isTheItemVisuallyInTheCenter={isTheItemVisuallyInTheCenter}
              $isNotAValidSelection={isNotAValidSelection}
              $selected={isSelected}
              $isUnset={!minutes}
              $readOnly={readOnly || applyAriaDisabled}
              $isFocusedWheel={isFocusedWheel}
              data-testid={
                ControlledDateTimePickerDatatestid.TIMEWHEEL[isTheItemVisuallyInTheCenter ? 'CURRENT_MINUTE' : 'MINUTE']
              }
              onClick={(e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) =>
                handleTimeWheelBtnChangeMinutes(minutesString, e)
              }
              tabIndex={tabIndex}
              type="button"
              {...btnProps}
              getOwnerProps={getProps}
            >
              {minutesString}
            </StyledTimeBtn>
          </StyledWheelListItem>
        );
      })}
      <StyledWheelListItem>
        <StyledWheelChangeTimeBtn
          aria-label="add one minute"
          buttonType="raw"
          size="m"
          onClick={handleNextMinute}
          innerRef={nextMinutesBtnRef}
          data-testid={ControlledDateTimePickerDatatestid.TIMEWHEEL.NEXT_MINUTE}
          tabIndex={-1}
          applyAriaDisabled={applyAriaDisabled}
          readOnly={readOnly}
          type="button"
          getOwnerProps={getProps}
        >
          <ChevronSmallDown color={arrowColor} />
        </StyledWheelChangeTimeBtn>
      </StyledWheelListItem>
    </StyledWheelList>
  );
};

export default MinutesList;
